import React from "react"
import "../styles.css"

/* ============================================================
   Help Center / FAQ content
   Each section groups related Q&A. `k` holds extra plain-text
   keywords so search matches answer content, not just the
   question text.
   ============================================================ */

const ExtLink = ({ href, children }) => (
  <a
    className="faq-link"
    href={href}
    target="_blank"
    rel="noopener noreferrer"
  >
    {children}
  </a>
)

/* Line-style SVG icons (stroke = currentColor, sized via CSS per container).
   Replaces the previous emoji glyphs while keeping the same icon-badge UI. */
const Icon = ({ name }) => {
  const common = {
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.8,
    strokeLinecap: "round",
    strokeLinejoin: "round",
    "aria-hidden": true,
  }
  switch (name) {
    case "about":
      return (
        <svg {...common}>
          <circle cx="12" cy="12" r="9" />
          <circle cx="12" cy="12" r="4.5" />
          <circle cx="12" cy="12" r="1" fill="currentColor" stroke="none" />
        </svg>
      )
    case "payments":
      return (
        <svg {...common}>
          <rect x="2.5" y="5" width="19" height="14" rx="2.5" />
          <path d="M2.5 9.5h19" />
          <path d="M6 15h4" />
        </svg>
      )
    case "balances":
      return (
        <svg {...common}>
          <path d="M4 20h16" />
          <path d="M6.5 20v-5" />
          <path d="M11 20V8.5" />
          <path d="M15.5 20v-8" />
          <path d="M20 20v-3" />
        </svg>
      )
    case "withdrawals":
      return (
        <svg {...common}>
          <path d="M13 4H6a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-7" />
          <path d="M14 4h6v6" />
          <path d="M20 4l-8.5 8.5" />
        </svg>
      )
    case "community":
      return (
        <svg {...common}>
          <circle cx="9" cy="8.5" r="3" />
          <path d="M3.5 19a5.5 5.5 0 0 1 11 0" />
          <path d="M16 6a3 3 0 0 1 0 5.5" />
          <path d="M17.5 13.5a5.5 5.5 0 0 1 3 4.9" />
        </svg>
      )
    case "security":
      return (
        <svg {...common}>
          <path d="M12 3l7 3v5c0 4.4-3 7.6-7 9-4-1.4-7-4.6-7-9V6l7-3z" />
          <path d="M9 12l2 2 4-4" />
        </svg>
      )
    case "support":
      return (
        <svg {...common}>
          <circle cx="12" cy="12" r="9" />
          <circle cx="12" cy="12" r="3.5" />
          <path d="M14.5 9.5l3-3" />
          <path d="M9.5 9.5l-3-3" />
          <path d="M14.5 14.5l3 3" />
          <path d="M9.5 14.5l-3 3" />
        </svg>
      )
    case "deposit":
      return (
        <svg {...common}>
          <path d="M12 3v10" />
          <path d="M8 9.5l4 4 4-4" />
          <path d="M4 16v2.5A1.5 1.5 0 0 0 5.5 20h13a1.5 1.5 0 0 0 1.5-1.5V16" />
        </svg>
      )
    case "search":
      return (
        <svg {...common}>
          <circle cx="11" cy="11" r="7" />
          <path d="M21 21l-4.3-4.3" />
        </svg>
      )
    case "warning":
      return (
        <svg {...common}>
          <path d="M12 4l8.6 14.7a1 1 0 0 1-.9 1.5H4.3a1 1 0 0 1-.9-1.5L12 4z" />
          <path d="M12 10v4" />
          <circle cx="12" cy="17" r="0.6" fill="currentColor" stroke="none" />
        </svg>
      )
    default:
      return null
  }
}

const FAQ_SECTIONS = [
  {
    id: "about",
    icon: "about",
    title: "About the Program",
    blurb: "How Project 31 works and why.",
    items: [
      {
        q: "What is Project 31?",
        k: "community driven bitcoin accumulation program pool resources fixed schedule accessible disciplined secure",
        a: (
          <p className="faq-p">
            Project 31 is a community-driven Bitcoin accumulation program. It
            allows you to contribute funds regularly, pool resources with
            others, and purchase Bitcoin together at a fixed schedule. The aim
            is to make Bitcoin ownership more accessible, disciplined, and
            secure.
          </p>
        ),
      },
      {
        q: "What are the benefits of the program?",
        k: "benefits simple sustainable timing market steady long term start $10 a month accessible practical financial freedom lower transaction costs accountability transparency",
        a: (
          <p className="faq-p">
            The program is designed to make Bitcoin accumulation simple and
            sustainable. It removes the stress of trying to time the market by
            encouraging steady, long-term contributions. You can start with as
            little as <strong>$10 a month</strong>, making participation both
            accessible and practical as a path toward financial freedom. By
            pooling funds, the program lowers transaction costs that would
            otherwise make small purchases uneconomical. It also creates
            accountability through a shared community rhythm, helping you stay
            consistent. Above all, it offers transparency, with your personal
            balance, transaction history, and the community's total Bitcoin
            holdings clearly visible on the dashboard.
          </p>
        ),
      },
      {
        q: "How does the monthly cycle work, and why monthly?",
        k: "monthly cycle fixed schedule payment window review confirmations pooled converted purchase reconciliation currencies",
        a: (
          <>
            <p className="faq-p">Each month follows a fixed schedule:</p>
            <ul className="faq-timeline">
              <li className="faq-timeline-item">
                <span className="faq-timeline-date">8th – 4th</span>
                <span className="faq-timeline-text">
                  The payment window is open. You log in to the dashboard, make
                  your payment, and confirm it.
                </span>
              </li>
              <li className="faq-timeline-item">
                <span className="faq-timeline-date">5th – 6th</span>
                <span className="faq-timeline-text">
                  Our team reviews all transfers, sends confirmations within
                  24–48 hours after the close of the payment window, and updates
                  dashboard balances.
                </span>
              </li>
              <li className="faq-timeline-item is-highlight">
                <span className="faq-timeline-date">7th</span>
                <span className="faq-timeline-text">
                  Contributions are pooled, converted into Bitcoin in a single
                  community purchase, and stored in the secure multi-signature
                  wallet.
                </span>
              </li>
            </ul>
            <p className="faq-p">
              We use a monthly schedule because it gives you sufficient time to
              contribute, allows smooth reconciliation across multiple
              currencies, and helps reduce unnecessary transaction costs. This
              balance of discipline, efficiency, and transparency makes the
              program both practical and sustainable.
            </p>
          </>
        ),
      },
      {
        q: "Why does the purchase happen on the 7th?",
        k: "purchase 7th reconcile payments confirm balances pooling one community transaction",
        a: (
          <p className="faq-p">
            The purchase happens on the 7th to allow enough time to reconcile
            payments and confirm balances before pooling funds into one
            community transaction.
          </p>
        ),
      },
      {
        q: "Where is the Bitcoin stored?",
        k: "bitcoin stored securely multi signature multisig wallet no single individual multiple authorizations",
        a: (
          <p className="faq-p">
            All Bitcoin is stored securely in a multi-signature wallet. No
            single individual can move funds independently, and multiple
            authorizations are required for any transfer.
          </p>
        ),
      },
    ],
  },
  {
    id: "payments",
    icon: "payments",
    title: "Payments",
    blurb: "Currencies, windows, fees and rates.",
    items: [
      {
        q: "What currencies are accepted?",
        k: "currencies accepted usd cad ngn naira canadian dollars us dollars stablecoins usdt usdc",
        a: (
          <>
            <p className="faq-p">We currently accept contributions in:</p>
            <ul className="faq-ul">
              <li>US Dollars (USD)</li>
              <li>Canadian Dollars (CAD)</li>
              <li>Nigerian Naira (NGN)</li>
              <li>Stablecoins (USDT, USDC)</li>
            </ul>
          </>
        ),
      },
      {
        q: "What are the payment windows?",
        k: "payment window opens 8th closes 4th following month included cycle bitcoin purchase",
        a: (
          <p className="faq-p">
            The payment window opens on the <strong>8th</strong> of each month
            and closes on the <strong>4th</strong> of the following month.
            Payments made within this period are included in that cycle's
            Bitcoin purchase.
          </p>
        ),
      },
      {
        q: "What happens if I pay after the 4th?",
        k: "pay after 4th not included current month purchase automatically rolled over next cycle",
        a: (
          <p className="faq-p">
            Payments received after the 4th are not included in the current
            month's purchase. They are automatically rolled over to the next
            cycle.
          </p>
        ),
      },
      {
        q: "Are there fees?",
        k: "fees no management fees bank charges blockchain network fees converting fiat to crypto",
        a: (
          <p className="faq-p">
            There are <strong>no management fees</strong>. Members only cover
            their own bank charges or blockchain network fees when sending funds
            and the cost of converting from fiat to crypto for individuals
            making fiat deposits.
          </p>
        ),
      },
      {
        q: "What is the exchange rate for Fiat (Naira, CAD, USD)?",
        k: "exchange rate fiat naira cad usd cannot provide in advance pooled purchase usdt best available market rate after close",
        a: (
          <p className="faq-p">
            We cannot provide an exchange rate in advance. All fiat
            contributions are pooled, and after the payment window closes on the
            4th, we purchase USDT at the best available market rate with the
            combined funds. Since this conversion happens after the close, the
            exact rate is not known ahead of time.
          </p>
        ),
      },
      {
        q: "What if I miss a month?",
        k: "miss a month skip no penalties resume contributions next cycle",
        a: (
          <p className="faq-p">
            If you skip a month, there are no penalties. You can resume
            contributions in the next cycle.
          </p>
        ),
      },
      {
        q: "Can I send more than the minimum I entered when I joined the waitlist?",
        k: "send more than minimum $10 larger amounts contributions above original commitment included next purchase",
        a: (
          <p className="faq-p">
            Yes. While the minimum is <strong>$10</strong>, you are free to
            contribute larger amounts at any time. Contributions above your
            original commitment are fully included in the next purchase.
          </p>
        ),
      },
      {
        q: "How do I confirm my payment?",
        k: "confirm payment log in dashboard project31.xyz confirm payment button fill form track reach out",
        a: (
          <p className="faq-p">
            After transferring funds, log in to your Project 31 dashboard at{" "}
            <ExtLink href="http://project31.xyz">project31.xyz</ExtLink>. Click
            the <strong>Confirm Payment</strong> button and fill in the required
            form. This step is very important as it allows our team to track
            your payment and reach out quickly if we notice any issues.
          </p>
        ),
      },
    ],
  },
  {
    id: "balances",
    icon: "balances",
    title: "Balances & Dashboard",
    blurb: "Tracking your holdings and history.",
    items: [
      {
        q: "When will my dashboard update?",
        k: "dashboard update not immediately after confirm reviewed transfer confirmation 5th 6th",
        a: (
          <p className="faq-p">
            Your dashboard will not update immediately after you click confirm.
            It is updated only after our team has reviewed your transfer and
            sent a confirmation — usually between the 5th and 6th.
          </p>
        ),
      },
      {
        q: "How do I track my balance?",
        k: "track balance dashboard personal bitcoin balance transaction history community total holdings",
        a: (
          <>
            <p className="faq-p">All tracking is done on the dashboard:</p>
            <ul className="faq-ul">
              <li>Your personal Bitcoin balance</li>
              <li>Your transaction history</li>
              <li>The community's total Bitcoin holdings</li>
            </ul>
          </>
        ),
      },
      {
        q: "What should I do if my payment doesn't reflect?",
        k: "payment doesn't reflect not reflected confirmation period contact support bitcoin@kudy.io",
        a: (
          <p className="faq-p">
            If your payment has not been reflected after the confirmation
            period, please contact our support team at{" "}
            <ExtLink href="mailto:bitcoin@kudy.io">bitcoin@kudy.io</ExtLink>.
          </p>
        ),
      },
      {
        q: "How is my Bitcoin balance calculated?",
        k: "bitcoin balance calculated allocation usd value confirmed contribution divided purchase price 7th",
        a: (
          <p className="faq-p">
            Your Bitcoin allocation is based on the USD value of your confirmed
            contribution, divided by the purchase price achieved on the 7th.
          </p>
        ),
      },
    ],
  },
  {
    id: "withdrawals",
    icon: "withdrawals",
    title: "Withdrawals",
    blurb: "Exiting the program and payouts.",
    items: [
      {
        q: "Can I withdraw my Bitcoin?",
        k: "withdraw bitcoin no 2031 target timeline multi signature wallet exit early prevailing price exchange rate next scheduled purchase fiat stablecoin partial withdrawals not allowed account closed",
        a: (
          <p className="faq-p">
            No. Bitcoin cannot be withdrawn directly before the program's target
            timeline of <strong>2031</strong>. Until then, all Bitcoin remains
            securely stored in the community multi-signature wallet. If you
            choose to exit early, your entire balance will be paid out using the
            prevailing Bitcoin price and exchange rate at the next scheduled
            purchase. Payouts are made in the same currency you deposited — fiat
            depositors receive fiat, stablecoin depositors receive stablecoin.
            Partial withdrawals are not allowed. Once this payout is made, your
            account will be closed and you will no longer participate in the
            program. To initiate a withdrawal, send an email to{" "}
            <ExtLink href="mailto:bitcoin@kudy.io">bitcoin@kudy.io</ExtLink>{" "}
            with your payout information.
          </p>
        ),
      },
    ],
  },
  {
    id: "community",
    icon: "community",
    title: "Community & Reporting",
    blurb: "Purchase reports and the community group.",
    items: [
      {
        q: "Where can I see reports of purchases?",
        k: "reports purchases community reports dashboard bitcoin price achieved total bitcoin acquired updated community balance",
        a: (
          <p className="faq-p">
            Community reports are shared on your dashboard after each purchase.
            They include details such as the Bitcoin price achieved, total
            Bitcoin acquired, and updated community balance.
          </p>
        ),
      },
      {
        q: "Is there a community group?",
        k: "community group private telegram join project 31 community",
        a: (
          <p className="faq-p">
            Yes. You can join our private Telegram group here:{" "}
            <ExtLink href="https://t.me/+eMORCxEdB003YWE0">
              Join the Project 31 Community
            </ExtLink>
            .
          </p>
        ),
      },
    ],
  },
  {
    id: "security",
    icon: "security",
    title: "Security",
    blurb: "How your Bitcoin is kept safe.",
    items: [
      {
        q: "What is a multi-signature wallet?",
        k: "multi signature multisig wallet approvals five signatories three approve no single person control security accountability",
        a: (
          <p className="faq-p">
            A multi-signature wallet is a Bitcoin wallet that requires approvals
            from multiple people before any transaction can be executed. In
            Project 31, the wallet has <strong>five signatories</strong>, and at
            least <strong>three</strong> of them must approve before Bitcoin can
            be moved. This ensures that no single person has control over the
            funds, adding both security and accountability for all members.
          </p>
        ),
      },
      {
        q: "Who are the signatories?",
        k: "signatories community representatives kudy operations team",
        a: (
          <p className="faq-p">
            The signatories are a mix of community representatives and members
            of the Kudy operations team.
          </p>
        ),
      },
      {
        q: "What happens if I forget to confirm payment?",
        k: "forget confirm payment transfer funds not confirmed dashboard contribution may not be included purchase always complete confirmation",
        a: (
          <p className="faq-p">
            If you transfer funds but do not confirm via the dashboard, your
            contribution may not be included in the purchase. Always complete
            the confirmation step to ensure your funds are tracked correctly.
          </p>
        ),
      },
    ],
  },
  {
    id: "support",
    icon: "support",
    title: "Support",
    blurb: "Getting help and stopping participation.",
    items: [
      {
        q: "How can I contact the team?",
        k: "contact team reach us bitcoin@kudy.io respond promptly payments confirmations dashboard concerns",
        a: (
          <p className="faq-p">
            You can reach us anytime at{" "}
            <ExtLink href="mailto:bitcoin@kudy.io">bitcoin@kudy.io</ExtLink>.
            Our team will respond promptly to assist with payments,
            confirmations, or dashboard concerns.
          </p>
        ),
      },
      {
        q: "What if I want to stop participating?",
        k: "stop participating voluntary stop contributing exit email payment details balance paid out price next bitcoin purchase",
        a: (
          <p className="faq-p">
            Participation in Project 31 is entirely voluntary. You may simply
            stop contributing at any time. To exit, send an email to{" "}
            <ExtLink href="mailto:bitcoin@kudy.io">bitcoin@kudy.io</ExtLink>{" "}
            with your payment details and your balance will be paid out at the
            price of the next Bitcoin purchase.
          </p>
        ),
      },
    ],
  },
]

const DEPOSIT_STEPS = [
  {
    title: "Log in to your dashboard",
    text: (
      <>
        Sign in at{" "}
        <ExtLink href="https://dashboard.kudy.io/">dashboard.kudy.io</ExtLink>.
      </>
    ),
  },
  {
    title: "Select Make Payment",
    text: "Choose your preferred currency — USD, CAD, NGN, USDT, or USDC.",
  },
  {
    title: "Transfer funds",
    text: "Send funds using the account or wallet details provided on the payment page.",
  },
  {
    title: "Confirm your payment",
    text: "Click Confirm Payment and fill in the required details so your contribution can be verified and credited.",
  },
]

/* A single collapsible question. */
const FaqItem = ({ id, question, answer, isOpen, onToggle }) => (
  <div className={`faq-item ${isOpen ? "open" : ""}`}>
    <button
      type="button"
      className="faq-q"
      aria-expanded={isOpen}
      aria-controls={`${id}-a`}
      onClick={onToggle}
    >
      <span className="faq-q-text">{question}</span>
      <span className="faq-q-icon" aria-hidden="true">
        <svg viewBox="0 0 24 24" width="20" height="20">
          <path
            d="M6 9l6 6 6-6"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </span>
    </button>
    <div className="faq-a" id={`${id}-a`} role="region">
      <div className="faq-a-inner">{answer}</div>
    </div>
  </div>
)

const FaqScreen = () => {
  const [query, setQuery] = React.useState("")
  const [openId, setOpenId] = React.useState(null)

  const normalizedQuery = query.trim().toLowerCase()
  const isSearching = normalizedQuery.length > 0

  // Flatten + filter for search results.
  const filteredSections = React.useMemo(() => {
    if (!isSearching) return FAQ_SECTIONS
    return FAQ_SECTIONS.map((section) => {
      const items = section.items.filter((item) => {
        const haystack = `${item.q} ${item.k || ""}`.toLowerCase()
        return haystack.includes(normalizedQuery)
      })
      return { ...section, items }
    }).filter((section) => section.items.length > 0)
  }, [isSearching, normalizedQuery])

  const totalMatches = React.useMemo(
    () => filteredSections.reduce((sum, s) => sum + s.items.length, 0),
    [filteredSections],
  )

  const toggle = (id) => setOpenId((prev) => (prev === id ? null : id))

  const scrollToSection = (id) => {
    const el = document.getElementById(`faq-sec-${id}`)
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" })
  }

  return (
    <div className="dashboard-content faq-page">
      {/* Hero */}
      <div className="faq-hero">
        <div className="faq-hero-badge">Help Center</div>
        <h1 className="faq-hero-title">How can we help?</h1>
        <p className="faq-hero-sub">
          Everything you need to know about contributing, confirming payments,
          balances, and security in Project 31.
        </p>

        <div className="faq-search">
          <svg
            className="faq-search-icon"
            viewBox="0 0 24 24"
            width="20"
            height="20"
            aria-hidden="true"
          >
            <circle
              cx="11"
              cy="11"
              r="7"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            />
            <path
              d="M21 21l-4.3-4.3"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
          <input
            type="text"
            className="faq-search-input"
            placeholder="Search questions… (e.g. fees, exchange rate, withdraw)"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label="Search frequently asked questions"
          />
          {isSearching && (
            <button
              type="button"
              className="faq-search-clear"
              onClick={() => setQuery("")}
              aria-label="Clear search"
            >
              ×
            </button>
          )}
        </div>
      </div>

      {/* Category chips (hidden while searching) */}
      {!isSearching && (
        <div className="faq-chips">
          {FAQ_SECTIONS.map((section) => (
            <button
              key={section.id}
              type="button"
              className="faq-chip"
              onClick={() => scrollToSection(section.id)}
            >
              <span className="faq-chip-icon" aria-hidden="true">
                <Icon name={section.icon} />
              </span>
              {section.title}
            </button>
          ))}
          <button
            type="button"
            className="faq-chip"
            onClick={() => scrollToSection("deposit")}
          >
            <span className="faq-chip-icon" aria-hidden="true">
              <Icon name="deposit" />
            </span>
            Deposit Guide
          </button>
        </div>
      )}

      {isSearching && (
        <div className="faq-result-count">
          {totalMatches > 0
            ? `${totalMatches} result${totalMatches === 1 ? "" : "s"} for “${query.trim()}”`
            : `No results for “${query.trim()}”`}
        </div>
      )}

      {/* Sections */}
      {filteredSections.length === 0 ? (
        <div className="faq-empty">
          <div className="faq-empty-icon">
            <Icon name="search" />
          </div>
          <div className="faq-empty-title">Nothing matched your search</div>
          <div className="faq-empty-text">
            Try a different keyword, or reach us at{" "}
            <ExtLink href="mailto:bitcoin@kudy.io">bitcoin@kudy.io</ExtLink>.
          </div>
        </div>
      ) : (
        filteredSections.map((section) => (
          <section
            key={section.id}
            id={`faq-sec-${section.id}`}
            className="faq-section"
          >
            <div className="faq-section-head">
              <span className="faq-section-icon" aria-hidden="true">
                <Icon name={section.icon} />
              </span>
              <div>
                <h2 className="faq-section-title">{section.title}</h2>
                <p className="faq-section-blurb">{section.blurb}</p>
              </div>
            </div>
            <div className="faq-list">
              {section.items.map((item, i) => {
                const id = `${section.id}-${i}`
                return (
                  <FaqItem
                    key={id}
                    id={id}
                    question={item.q}
                    answer={item.a}
                    isOpen={openId === id}
                    onToggle={() => toggle(id)}
                  />
                )
              })}
            </div>
          </section>
        ))
      )}

      {/* Deposit guide (hidden while searching) */}
      {!isSearching && (
        <section id="faq-sec-deposit" className="faq-section faq-deposit">
          <div className="faq-section-head">
            <span className="faq-section-icon" aria-hidden="true">
              <Icon name="deposit" />
            </span>
            <div>
              <h2 className="faq-section-title">Deposit Guide</h2>
              <p className="faq-section-blurb">
                Complete all deposits through your Project 31 dashboard.
              </p>
            </div>
          </div>

          <div className="faq-steps">
            {DEPOSIT_STEPS.map((step, i) => (
              <div className="faq-step" key={i}>
                <div className="faq-step-num">{i + 1}</div>
                <div className="faq-step-body">
                  <div className="faq-step-title">{step.title}</div>
                  <div className="faq-step-text">{step.text}</div>
                </div>
              </div>
            ))}
          </div>

          <div className="faq-notes">
            <div className="faq-note">
              <span className="faq-note-mark" aria-hidden="true">
                <Icon name="warning" />
              </span>
              <span>
                For fiat transactions (USD, CAD, NGN), please avoid using
                “Bitcoin,” “Crypto,” or similar terms in the payment reference to
                prevent delays with banks and payment providers.
              </span>
            </div>
            <div className="faq-note">
              <span className="faq-note-mark" aria-hidden="true">
                <Icon name="warning" />
              </span>
              <span>
                Always ensure you select the correct currency and network before
                sending stablecoins.
              </span>
            </div>
          </div>
        </section>
      )}

      {/* Support CTA */}
      <div className="faq-support">
        <div className="faq-support-glow" />
        <div className="faq-support-text">
          <h3 className="faq-support-title">Still have questions?</h3>
          <p className="faq-support-sub">
            Our team is here to help with payments, confirmations, and dashboard
            concerns.
          </p>
        </div>
        <div className="faq-support-actions">
          <a className="faq-support-btn primary" href="mailto:bitcoin@kudy.io">
            Email Support
          </a>
          <a
            className="faq-support-btn"
            href="https://t.me/+eMORCxEdB003YWE0"
            target="_blank"
            rel="noopener noreferrer"
          >
            Join Telegram
          </a>
        </div>
      </div>
    </div>
  )
}

export default FaqScreen
